class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]'))
      .reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = this.debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));
    this.addEventListener('keydown', this.onKeyDown.bind(this));
    
    // Handle form submission for updates only
    const cartForm = this.querySelector('#cart');
    if (cartForm) {
      cartForm.addEventListener('submit', this.onFormSubmit.bind(this));
    }
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    const onUpdate = (event) => {
      if (!event || event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    };

    // This component carries its own DOM-event pubsub, which is a separate
    // channel from the theme-wide pubsub.js used by product-form, cart-drawer
    // and wishlist quick-add. Listen on both so an add from anywhere refreshes
    // the cart table.
    this.cartUpdateUnsubscriber = this.subscribe('cartUpdate', onUpdate);

    // This file is loaded without `defer`, so pubsub.js/constants.js are not
    // parsed yet at this point — binding immediately would silently no-op.
    // Wait until every deferred script has run.
    const bindGlobal = () => {
      if (this.globalCartUpdateUnsubscriber) return;
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this.globalCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, onUpdate);
    };

    bindGlobal();
    if (!this.globalCartUpdateUnsubscriber) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bindGlobal, { once: true });
      } else {
        window.setTimeout(bindGlobal, 0);
      }
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    if (this.globalCartUpdateUnsubscriber) {
      this.globalCartUpdateUnsubscriber();
    }
  }

  onKeyDown(event) {
    if (event.key !== 'Enter' || !event.target.classList.contains('quantity__input')) return;

    event.preventDefault();
    event.stopPropagation();
    event.target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onChange(event) {
    if (event.target.classList.contains('quantity__input')) {
      const input = event.target;
      let newQuantity = parseInt(input.value);
      const maxQuantity = parseInt(input.getAttribute('max')) || 999999;
      const minQuantity = parseInt(input.getAttribute('min')) || 0;

      if (Number.isNaN(newQuantity)) {
        input.value = input.getAttribute('value');
        return;
      }
      
      // Validate quantity doesn't exceed maximum
      if (newQuantity > maxQuantity) {
        input.value = maxQuantity;
        newQuantity = maxQuantity;
        this.showQuantityError(input, `Maximum quantity available: ${maxQuantity}`);
      }
      
      // Validate quantity is not less than minimum
      if (newQuantity < minQuantity) {
        input.value = minQuantity;
        newQuantity = minQuantity;
      }
      
      this.updateQuantity(event.target.dataset.index, newQuantity, event.target.name, input.dataset.lineKey);
    }
  }

  showQuantityError(input, message) {
    // Remove any existing error
    this.clearQuantityError(input);
    
    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'quantity-error';
    errorElement.textContent = message;
    errorElement.style.cssText = 'color: #ff6b6b; font-size: 12px; margin-top: 4px; text-transform: uppercase;';
    
    // Insert after the quantity input
    input.parentNode.insertBefore(errorElement, input.nextSibling);
    
    // Remove error after 3 seconds
    setTimeout(() => this.clearQuantityError(input), 3000);
  }

  clearQuantityError(input) {
    const errorElement = input.parentNode.querySelector('.quantity-error');
    if (errorElement) {
      errorElement.remove();
    }
  }

  onFormSubmit(event) {
    // Only prevent default for updates, not checkout
    if (event.submitter && event.submitter.name === 'update') {
      event.preventDefault();
      this.updateCart();
    }
    // For checkout (formaction="/checkout"), let the form submit normally
  }

  updateCart() {
    const formData = new FormData(this.querySelector('#cart'));
    
    fetch(window.routes.cart_url, {
      method: 'POST',
      body: formData
    })
    .then(response => response.text())
    .then(() => {
      // Refresh the page to show updated cart
      window.location.reload();
    })
    .catch(error => {
      console.error('Error updating cart:', error);
    });
  }

  reinitCartSliders() {
    if (window.BazarBizarRecentlyViewed) {
      window.BazarBizarRecentlyViewed.render();
    }
    if (window.__collectionSlider) {
      window.__collectionSlider.initAll(this);
    }
  }

  onCartUpdate() {
    return fetch(window.location.href)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (!sourceQty) return;

        this.innerHTML = sourceQty.innerHTML;

        this.reinitCartSliders();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    const sections = [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      }
    ];

    // Re-render the cart section itself so removing a line updates the table,
    // totals and sliders in place instead of reloading the page.
    const cartSection = this.closest('.shopify-section');
    if (cartSection && cartSection.id) {
      sections.push({
        id: cartSection.id,
        section: cartSection.id.replace('shopify-section-', ''),
        selector: '.cart__body'
      });
    }

    return sections;
  }

  updateQuantity(line, quantity, name, lineKey) {
    this.enableLoading(line);

    const body = JSON.stringify({
      updates: {
        [lineKey || line]: quantity
      },
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${window.routes.cart_update_url}`, {...this.fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text().then((state) => ({
          ok: response.ok,
          state
        }));
      })
      .then(({ ok, state }) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll('.cart__product');

        if (!ok || parsedState.errors || !parsedState.sections) {
          const message = parsedState.errors || parsedState.description || parsedState.message || window.cartStrings.error;
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, message);
          this.onCartUpdate();
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          const element = document.getElementById(section.id);
          if (!element || !parsedState.sections[section.section]) return;

          const elementToReplace = element.querySelector(section.selector) || element;
          const sectionHtml = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

          if (section.id === 'cart-icon-bubble') {
            if (typeof window.updateHeaderCartIconFromSection === 'function') {
              window.updateHeaderCartIconFromSection(sectionHtml);
            }
            return;
          }

          elementToReplace.innerHTML = sectionHtml;
        }));

        // Replacing .cart__body wipes the sliders that live inside it: their
        // listeners are gone and the Recently viewed track comes back empty and
        // still hidden, which also left the estimator sitting against a
        // collapsed block. Rebuild them against the fresh markup.
        this.reinitCartSliders();

        // The cart section is re-rendered above, so the line list and totals are
        // already current. Only the empty state needs a full render (it lives in
        // the other branch of the section's `unless cart == empty`).
        if (parsedState.item_count === 0) {
          location.reload();
        }

        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? this.trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          this.trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart__product') && cartDrawerWrapper) {
          this.trapFocus(cartDrawerWrapper, document.querySelector('.cart__product'));
        }

        this.publish('cartUpdate', {source: 'cart-items'});
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart__product-error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  updateLineItem(line, item) {
    const lineElement = document.getElementById(`CartItem-${line}`);
    if (!lineElement || !item) {
      if (lineElement) lineElement.remove();
      return;
    }

    // Update quantity
    const quantityInput = lineElement.querySelector('.quantity__input');
    if (quantityInput) quantityInput.value = item.quantity;

    // Update line price
    const linePriceElement = lineElement.querySelector('.cart__product-total-price');
    if (linePriceElement && window.Shopify && window.Shopify.formatMoney) {
      linePriceElement.textContent = window.Shopify.formatMoney(item.line_price, window.Shopify.money_format || '{{amount}}');
    }

    // Update cart totals (find and update subtotal)
    this.updateCartTotals();
  }

  updateCartTotals() {
    fetch(window.location.href)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newCartTotal = doc.querySelector('.cart__info-checkout-title');
        const currentCartTotal = document.querySelector('.cart__info-checkout-title');
        
        if (newCartTotal && currentCartTotal) {
          currentCartTotal.innerHTML = newCartTotal.innerHTML;
        }
      })
      .catch(console.error);
  }

  enableLoading(line) {
    const mainCartItems = this.closest('.cart') || this;
    if (mainCartItems && mainCartItems.classList) {
      mainCartItems.classList.add('cart__items--disabled');
    }

    const cartItem = document.getElementById(`CartItem-${line}`);
    if (cartItem) {
      cartItem.style.opacity = '0.5';
      cartItem.style.pointerEvents = 'none';
    }

    document.activeElement.blur();
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }
  }

  disableLoading(line) {
    const mainCartItems = this.closest('.cart') || this;
    if (mainCartItems && mainCartItems.classList) {
      mainCartItems.classList.remove('cart__items--disabled');
    }

    const cartItem = document.getElementById(`CartItem-${line}`);
    if (cartItem) {
      cartItem.style.opacity = '1';
      cartItem.style.pointerEvents = 'auto';
    }
  }

  fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
    };
  }

  debounce(func, wait, immediate) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  }

  publish(eventName, payload) {
    document.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
  }

  subscribe(eventName, callback) {
    const handler = (event) => callback(event.detail);
    document.addEventListener(eventName, handler);
    return () => document.removeEventListener(eventName, handler);
  }

  trapFocus(container, elementToFocus = container) {
    var elements = this.getFocusableElements(container);
    var first = elements[0];
    var last = elements[elements.length - 1];

    this.removeTrapFocus();

    this.trapFocusHandlers = {
      focusin: (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;

        document.addEventListener('keydown', this.trapFocusHandlers.keydown);
      },

      focusout: function() {
        document.removeEventListener('keydown', this.trapFocusHandlers.keydown);
      }.bind(this),

      keydown: function(event) {
        if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      }.bind(this)
    };

    document.addEventListener('focusout', this.trapFocusHandlers.focusout);
    document.addEventListener('focusin', this.trapFocusHandlers.focusin);

    elementToFocus.focus();
  }

  getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  }

  removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', this.trapFocusHandlers?.focusin);
    document.removeEventListener('focusout', this.trapFocusHandlers?.focusout);
    document.removeEventListener('keydown', this.trapFocusHandlers?.keydown);

    if (elementToFocus) elementToFocus.focus();
  }
}

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, this.dataset.name || 'updates[]', this.dataset.lineKey);
    });
  }
}

// Only define QuantityInput if it doesn't exist
if (!customElements.get('quantity-input')) {
  class CartQuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true })
      this.input.addEventListener('change', this.onInputChange.bind(this));
      this.querySelectorAll('button').forEach(
        (button) => button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    onInputChange(event) {
      this.validateQtyRules();
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;

      if (event.target.name === 'plus') {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }

      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        const buttonMinus = this.querySelector("[name='minus']");
        if (buttonMinus) buttonMinus.disabled = value <= min;
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        const buttonPlus = this.querySelector("[name='plus']");
        if (buttonPlus) buttonPlus.disabled = value >= max;
      }
    }
  }
  customElements.define('quantity-input', CartQuantityInput);
}

customElements.define('cart-items', CartItems);
customElements.define('cart-remove-button', CartRemoveButton); 
if (typeof window !== 'undefined' && !window.updateHeaderCartIconFromSection) {
  window.updateHeaderCartIconFromSection = function(sectionHtml) {
    if (!sectionHtml) return;

    const cartTrigger = document.getElementById('cart-icon-bubble');
    if (!cartTrigger) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sectionHtml, 'text/html');
    const svg = cartTrigger.querySelector('svg');

    const nextText = doc.querySelector('[data-cart-count-text]');
    const nextBubble = doc.querySelector('.cart-count-bubble');

    const currentText = cartTrigger.querySelector('[data-cart-count-text]');
    const currentBubble = cartTrigger.querySelector('.cart-count-bubble');

    if (nextText) {
      const cloneText = nextText.cloneNode(true);
      if (currentText) {
        currentText.replaceWith(cloneText);
      } else if (svg) {
        svg.insertAdjacentElement('afterend', cloneText);
      } else {
        cartTrigger.insertAdjacentElement('afterbegin', cloneText);
      }
    } else if (currentText) {
      currentText.remove();
    }

    if (nextBubble) {
      const cloneBubble = nextBubble.cloneNode(true);
      const targetText = cartTrigger.querySelector('[data-cart-count-text]');
      if (currentBubble) {
        currentBubble.replaceWith(cloneBubble);
      } else if (targetText) {
        targetText.insertAdjacentElement('afterend', cloneBubble);
      } else if (svg) {
        svg.insertAdjacentElement('afterend', cloneBubble);
      } else {
        cartTrigger.appendChild(cloneBubble);
      }
    } else if (currentBubble) {
      currentBubble.remove();
    }
  };
}
